import { FC, useMemo } from "react";
import {
  GetAnatomyItemChoicesQuery,
  GetOrderByUniqueAttributeQuery,
  GetUserProductPreferencesQuery,
  TeethShadeSide,
  TeethShadeType,
} from "/@/generated/graphql";
import { CreateOrderAnatomyItemPreferences } from "./CreateOrderAnatomyPreferences";
import { CreateAnatomyItemStep } from "../../../../shared/types/orders/createOrder.types";
import { CreateOrderAnatomyItemInfo } from "./CreateOrderAnatomyItemInfo";
import { Grid, Space, Stepper } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { CreateProductSummarySideBar } from "../CreateProductSummarySideBar";
import { useCreateOrderStore } from "/@/shared/stores/orders/create-store.store";
import {
  ProductMaterialCharacteristicsForm,
  validateSchema,
} from "@jasper/forms";
import {
  ItemShade,
  useDentistProductPreferences,
  useUpsertItem,
} from "@jasper/use-cases";
import {
  DentistProductPreferences,
  MaterialCharacteristics,
  materialCharacteristicsSchema,
  ProductType,
  DeepPartial,
  SOLIDARIZED_PRODUCT_TYPE,
  areMaterialCharacteristicsEqual,
  ProductTypeEnum,
} from "@jasper/client-models";
import { useForm } from "@tanstack/react-form";

import { StepsButtons } from "../../../../components/StepsButtons";
import client from "../../../../apolloClient";
import { notifications } from "@mantine/notifications";

type CreateOrderAnatomyItemsProps = {
  product: NonNullable<
    GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"]["products"]
  >[0];
  allProducts: NonNullable<
    GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"]["products"]
  >;
  patient: GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"]["patient"];
  userProductPreferences: GetUserProductPreferencesQuery;
  anatomyItemChoicesData: GetAnatomyItemChoicesQuery;
  order: GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"];
  setIsLoading: (isLoading: boolean) => void;
};

type ItemMaterialCharacteristics = {
  tooth: number;
  materialCharacteristics: MaterialCharacteristics;
};

type ProductMaterialCharacteristics = {
  items: ItemMaterialCharacteristics[];
};

const computeDefaultMaterialCharacteristics = ({
  accountProductPreferences,
  item,
  itemShades,
}: {
  accountProductPreferences?: DentistProductPreferences;
  item?: NonNullable<
    NonNullable<
      GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"]["products"]
    >[0]["anatomyItem"]
  >[0];
  itemShades: ItemShade[];
}): DeepPartial<MaterialCharacteristics> => {
  const isMultiShade = item?.teethshadeType === TeethShadeType.MultiShade;

  const defaultShadeType =
    itemShades.find(itemShade => itemShade.value === item?.itemShade?.id)
      ?.shadeType ?? accountProductPreferences?.shadeType;

  const getShade = ({ shade }: { shade: string }) => {
    return itemShades.find(
      itemShade =>
        itemShade.value === shade && itemShade.shadeType === defaultShadeType
    )?.value;
  };

  const getToothShadeSide = ({
    toothShadeSide,
  }: {
    toothShadeSide: TeethShadeSide;
  }) => {
    const shade = item?.multiShadeInfo?.find(
      multiShadeInfo => multiShadeInfo.teethShadeSide === toothShadeSide
    )?.itemShade?.id;
    return isMultiShade && shade ? getShade({ shade }) : undefined;
  };

  return {
    material: item?.itemMaterial?.id,
    shadeCharacteristics:
      item?.itemShade || item?.multiShadeInfo?.length
        ? {
            shade: isMultiShade
              ? undefined
              : getShade({ shade: item?.itemShade?.id ?? "" }),
            shadeType: defaultShadeType,
            isMultiShade,
            gingivalShade: getToothShadeSide({
              toothShadeSide: TeethShadeSide.Gingival,
            }),
            baseShade: getToothShadeSide({
              toothShadeSide: TeethShadeSide.Base,
            }),
            incisalShade: getToothShadeSide({
              toothShadeSide: TeethShadeSide.Incisal,
            }),
          }
        : undefined,
  };
};

const CreateOrderAnatomyItemInfoV2 = ({
  order,
  product,
  productType,
}: { productType: ProductType } & Pick<
  CreateOrderAnatomyItemsProps,
  "product" | "order"
>) => {
  const {
    isLoading: isCreateAnatomyItemLoading,
    itemMaterials,
    itemShadeTypes,
    itemShades,
    upsertItems,
    isUpserting,
    isDeleting,
    deleteItems,
  } = useUpsertItem({
    item: {
      type: productType,
    },
    productId: product.id,
  });
  const {
    isLoading: isDentistProductPreferencesLoading,
    dentistProductPreferences,
  } = useDentistProductPreferences({ dentistId: order.user.id });

  const [
    createProductIndex,
    updateCreateProductIndex,
    updateCreateProductStep,
  ] = useCreateOrderStore(state => [
    state.createProductIndex,
    state.updateCreateProductIndex,
    state.updateCreateProductStep,
  ]);

  const items = useMemo(
    () =>
      product.anatomyItem?.filter(item => item.itemType === productType) ?? [],
    [product.anatomyItem, productType]
  );

  const defaultValues: {
    productMaterialCharacteristics: DeepPartial<ProductMaterialCharacteristics>;
  } = {
    productMaterialCharacteristics: {
      items: (product?.teeth ?? []).map((tooth: number) => {
        const savedItemForTooth =
          items.find(item => item.teeth.includes(tooth)) ?? items[0];

        return {
          tooth,
          materialCharacteristics: computeDefaultMaterialCharacteristics({
            accountProductPreferences: dentistProductPreferences,
            item: savedItemForTooth,
            itemShades,
          }),
        };
      }),
    },
  };

  const form = useForm({
    onSubmit: async ({ value }) => {
      const items = value.productMaterialCharacteristics?.items ?? [];

      try {
        if (
          product?.anatomyItem &&
          product.anatomyItem.filter(item => item.itemType === productType)
            .length > items.length
        ) {
          const itemsToDelete = product.anatomyItem
            .filter(item => item.itemType === productType)
            .slice(items.length);

          await deleteItems(itemsToDelete.map(item => item.id));
        }

        await upsertItems(
          (() => {
            const consumedSavedItemIds = new Set<string>();

            return items.map(item => {
              const matchingSavedItem = product.anatomyItem?.find(savedItem => {
                if (
                  !savedItem.teeth.includes(Number(item.tooth)) ||
                  consumedSavedItemIds.has(savedItem.id) ||
                  savedItem.itemType !== productType
                ) {
                  return false;
                }

                return true;
              });

              if (matchingSavedItem?.id) {
                consumedSavedItemIds.add(matchingSavedItem.id);
              }

              return {
                materialCharacteristics:
                  item.materialCharacteristics as MaterialCharacteristics,
                teeth: [Number(item.tooth)],
                itemId: matchingSavedItem?.id,
              };
            });
          })()
        );
      } catch {
        notifications.show({
          title: `${productType} création a échoué`,
          message:
            "Une erreur est survenue lors de la création de la prothèse. Veuillez réessayer.",
          color: "red",
        });
      }

      await client.refetchQueries({
        include: ["getOrderByUniqueAttribute"],
      });

      updateCreateProductStep(CreateAnatomyItemStep.Preferences);
    },
    defaultValues,
  });

  if (isCreateAnatomyItemLoading || isDentistProductPreferencesLoading) {
    return null;
  }

  return (
    <>
      <form.Field
        name="productMaterialCharacteristics"
        validators={{
          onSubmit: ({ value }) => {
            const { items = [] } = value ?? {};

            const itemErrors = items.map(item => {
              const result = validateSchema(
                materialCharacteristicsSchema,
                item.materialCharacteristics
              );

              if (result.success === true) {
                return undefined;
              }

              return {
                materialCharacteristics: result.errors,
              };
            });

            if (!itemErrors?.some(Boolean)) {
              return;
            }

            return {
              items: itemErrors,
            };
          },
        }}
      >
        {field => {
          const onSubmitErrors = field.state.meta.errorMap.onSubmit;
          const productMaterialCharacteristics = field.state.value;

          // Product is per tooth if items have different material or shade characteristics
          const isPerTooth =
            productMaterialCharacteristics?.items &&
            productMaterialCharacteristics.items.length > 0
              ? productMaterialCharacteristics.items.some(item => {
                  if (
                    !item.materialCharacteristics ||
                    !productMaterialCharacteristics.items?.[0]
                      ?.materialCharacteristics
                  ) {
                    return (
                      item.materialCharacteristics !==
                      productMaterialCharacteristics.items?.[0]
                        .materialCharacteristics
                    );
                  }

                  return !areMaterialCharacteristicsEqual(
                    item.materialCharacteristics,
                    productMaterialCharacteristics.items![0]
                      .materialCharacteristics
                  );
                })
              : false;

          return (
            <ProductMaterialCharacteristicsForm
              productType={productType}
              isSolidarized={SOLIDARIZED_PRODUCT_TYPE.includes(productType)}
              defaultIsPerTooth={isPerTooth}
              itemMaterials={itemMaterials}
              itemShadeTypes={itemShadeTypes}
              itemShades={itemShades}
              teeth={product?.teeth ?? []}
              value={productMaterialCharacteristics}
              errors={
                typeof onSubmitErrors === "object"
                  ? (onSubmitErrors as any)
                  : undefined
              }
              onChange={next => field.setValue(next)}
              validateTooth={(tooth: number) => {
                const toothCharacteristics =
                  productMaterialCharacteristics?.items?.find(
                    item => item.tooth === tooth
                  )?.materialCharacteristics;
                const res = validateSchema(
                  materialCharacteristicsSchema,
                  toothCharacteristics
                );

                if (res.success === true) {
                  return;
                }

                return res.errors;
              }}
            />
          );
        }}
      </form.Field>
      <Space h="xl" />
      <StepsButtons
        handleSubmit={form.handleSubmit}
        setPreviousAction={updateCreateProductIndex}
        previousStep={createProductIndex - 1}
        isFirst={createProductIndex <= 0}
        isLoading={isUpserting || isDeleting}
      />
    </>
  );
};

export const CreateOrderAnatomyItems: FC<CreateOrderAnatomyItemsProps> = ({
  patient,
  product,
  userProductPreferences,
  anatomyItemChoicesData,
  order,
  allProducts,
  setIsLoading,
}) => {
  const createProductStep = useCreateOrderStore(
    state => state.createProductStep
  );

  const { classes } = useStyles();
  const dataContent = () => {
    switch (createProductStep) {
      case CreateAnatomyItemStep.Item:
        if (product.productType === ProductTypeEnum.bridge) {
          return (
            <CreateOrderAnatomyItemInfoV2
              product={product}
              order={order}
              productType={product.productType as any}
            />
          );
        }
        return (
          <CreateOrderAnatomyItemInfo
            product={product}
            anatomyItemChoicesData={anatomyItemChoicesData}
            allProducts={allProducts}
            order={order}
            setIsLoading={setIsLoading}
          />
        );
      case CreateAnatomyItemStep.Preferences:
        return (
          <CreateOrderAnatomyItemPreferences
            product={product}
            userProductPreferences={userProductPreferences}
            setIsLoading={setIsLoading}
          />
        );
      default:
        return <div>ERROR create anatomy item</div>;
    }
  };

  return (
    <Grid columns={36} gap={0} style={{ height: "100%" }}>
      <Grid.Col
        span={createProductStep < CreateAnatomyItemStep.Review ? 26 : 36}
        style={theme => ({
          padding: theme.spacing.lg,
          height: "fit-content",
        })}
      >
        <Stepper
          active={createProductStep}
          classNames={{
            step: classes.stepper,
            stepBody: classes.stepBody,
          }}
        >
          <Stepper.Step label="Prothèse" />
          <Stepper.Step label="Préférences" />
        </Stepper>
        <Space h="xl" />
        {dataContent()}
      </Grid.Col>
      {createProductStep !== CreateAnatomyItemStep.Review && (
        <Grid.Col
          span={10}
          style={theme => ({
            padding: theme.spacing.sm,
            backgroundColor: theme.colors.primaryLight[9],
          })}
        >
          <CreateProductSummarySideBar
            product={product}
            patient={patient}
            order={order}
          />
        </Grid.Col>
      )}
    </Grid>
  );
};

const useStyles = createStyles(theme => ({
  stepper: {
    flexDirection: "column",
  },
  stepBody: {
    marginLeft: 0,
    marginTop: theme.spacing.xs,
    alignItems: "center",
  },
}));
